import { useEffect, useRef, useState } from 'react';

/**
 * The ReleaseTEAM site navigation bar, ported from the live DOM at
 * https://www.releaseteam.com/ (captured 2026-09-26, re-synced 2026-09-26 to
 * the site's new two-level menu) so DevTool Detective carries the same
 * navigation every other ReleaseTEAM property does. See "keeping this in
 * sync" below.
 *
 * This is a from-scratch port of the markup/CSS/behaviour, not an iframe or a
 * remote include: the CSP here permits no third-party requests at all
 * (`PRIVACY.md`), so the logo is self-hosted (`/brand/releaseteam-logo-white.png`)
 * and the type is the same self-hosted Open Sans variable font the rest of the
 * app already loads (`src/styles/app.css`). Outbound `<a href>` links to
 * www.releaseteam.com remain — those are navigations the player initiates by
 * clicking, not requests the page makes on load, so they do not touch the "no
 * third-party requests" claim in `PRIVACY.md` (see the rules-for-changing-this
 * section there).
 *
 * Rendered on every non-gameplay screen (including the title screen), always
 * as a direct child of `.panel`, BEFORE `.panel__inner` — never nested inside
 * it. `.panel__inner` is a centred `max-width` column; `.panel` itself is
 * already edge-to-edge (`position: fixed; inset: 0`) with a static, non-`vw`
 * padding (`20px 16px`, see `app.css`). Escaping only that static padding
 * (fixed pixel negative margins) makes the bar match `.panel`'s own content
 * box (`clientWidth`) at every breakpoint. An earlier version nested this
 * inside `.panel__inner` and broke out with `left: 50%; margin-left: -50vw`,
 * which overflowed the page horizontally on any desktop width wide enough to
 * show a scrollbar (`100vw` includes the scrollbar's own width) — never
 * reintroduce a `vw`-based breakout here.
 *
 * The bar's width targets `.panel`'s own `clientWidth`, never
 * `document.documentElement.clientWidth`. `.panel` is the element that
 * actually scrolls (`overflow-y: auto`) and draws its own vertical scrollbar
 * along its own right inner edge whenever its content is taller than the
 * viewport, which is most panel screens. A later version of this component
 * measured that scrollbar's width in JS and stretched the bar to compensate,
 * to reach `document.documentElement.clientWidth` exactly — but that pushes
 * the bar's right edge past `.panel`'s content box and under `.panel`'s own
 * scrollbar, which makes `.panel` itself scrollable sideways (confirmed live,
 * 2026-09-26: `.panel`'s `scrollLeft` no longer stayed at 0). The bar must
 * end exactly where `.panel`'s own scrollbar begins, the same way the main
 * site's header ends at the page's own scrollbar — never reintroduce a
 * scrollbar-width compensation.
 *
 * Behaviour matches the reference implementation captured in the scratchpad:
 * desktop shows hover/focus-driven dropdowns with no JS required; below the
 * 980px breakpoint (the live site's own Divi breakpoint) the menu collapses
 * behind a hamburger and each item's submenu becomes a one-at-a-time
 * accordion. State lives here in React instead of the vanilla-JS reference
 * because this is a React app, but the interaction contract — Escape closes,
 * only one submenu open at a time, closing the mobile menu on resize past the
 * breakpoint — is the same.
 */

interface NavLink {
  href: string;
  label: string;
}

interface NavItem {
  id: string;
  href: string;
  label: string;
  sub?: NavLink[];
  wide?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'professional-services',
    href: 'https://www.releaseteam.com/expert-devops-service/',
    label: 'Professional Services',
    sub: [
      { href: 'https://www.releaseteam.com/atlassian-support-services/', label: 'Atlassian Services' },
      { href: 'https://www.releaseteam.com/gitlab-consulting-and-gitlab-support-services/', label: 'GitLab Services' },
      { href: 'https://www.releaseteam.com/github-support-and-consulting-services/', label: 'GitHub Services' },
      { href: 'https://www.releaseteam.com/red-hat-support-services/', label: 'Red Hat Services' },
      { href: 'https://www.releaseteam.com/amazon-aws-sales-and-support/', label: 'AWS Services' },
    ],
  },
  {
    id: 'consulting-services',
    href: 'https://www.releaseteam.com/devops-consulting/',
    label: 'Consulting Services',
    sub: [
      { href: 'https://www.releaseteam.com/devops-support/', label: 'Expert Support' },
      { href: 'https://www.releaseteam.com/devops-staffing-and-training/', label: 'Staff Augmentation' },
      {
        href: 'https://www.releaseteam.com/devops-implementations-and-migrations/',
        label: 'Tool Migrations',
      },
      { href: 'https://www.releaseteam.com/devops-tool-evaluations/', label: 'Tool Evaluation & Selection' },
      { href: 'https://www.releaseteam.com/devops-assessments/', label: 'DevOps Assessments' },
      { href: 'https://www.releaseteam.com/ai-devops/', label: 'AI Capabilities & Insights' },
    ],
  },
  {
    id: 'service-management',
    href: 'https://www.releaseteam.com/atlassian-jira-service-management/',
    label: 'Service Management',
    sub: [
      {
        href: 'https://www.releaseteam.com/jira-service-management-support-and-consulting/',
        label: 'JSM Support & Consulting',
      },
      { href: 'https://www.releaseteam.com/atlassian-service-collection/', label: 'Atlassian Service Collection' },
      {
        href: 'https://www.releaseteam.com/legacy-itsm-to-jsm-migration-services/',
        label: 'Legacy ITSM to JSM Migration',
      },
      { href: 'https://www.releaseteam.com/itsm/', label: 'ITSM Solutions' },
      { href: 'https://www.releaseteam.com/service-management-guides/', label: 'Guides & Comparisons' },
    ],
  },
  {
    id: 'software',
    href: 'https://www.releaseteam.com/devops-software-solutions/',
    label: 'Software',
    sub: [
      { href: 'https://www.releaseteam.com/source-control-and-ci-cd/', label: 'Source Control & CI/CD' },
      { href: 'https://www.releaseteam.com/planning-and-collaboration/', label: 'Planning & Collaboration' },
      { href: 'https://www.releaseteam.com/cloud-and-infrastructure/', label: 'Cloud & Infrastructure' },
      { href: 'https://www.releaseteam.com/devsecops/', label: 'Security & Backup' },
      { href: 'https://www.releaseteam.com/partners/', label: 'Products' },
    ],
  },
  {
    id: 'resources',
    href: 'https://www.releaseteam.com/devops-resources/',
    label: 'Resources',
    sub: [
      { href: 'https://www.releaseteam.com/devops-case-studies/', label: 'Case Studies' },
      { href: 'https://www.releaseteam.com/blog/', label: 'Blog' },
      { href: 'https://www.releaseteam.com/devops-whitepapers/', label: 'White Papers & Downloads' },
      { href: 'https://www.releaseteam.com/newsletter/', label: 'Newsletter' },
      { href: 'https://www.releaseteam.com/tools/', label: 'Tools' },
      { href: 'https://www.releaseteam.com/boot-camp/', label: 'Training & Boot Camps' },
    ],
  },
  {
    id: 'about',
    href: 'https://www.releaseteam.com/about/',
    label: 'About',
    sub: [
      { href: 'https://www.releaseteam.com/about/', label: 'About ReleaseTEAM' },
      { href: 'https://www.releaseteam.com/careers/', label: 'Careers' },
      { href: 'https://www.releaseteam.com/clients/', label: 'Clients' },
      { href: 'https://www.releaseteam.com/partners/', label: 'Partners' },
      { href: 'https://www.releaseteam.com/about#faqs', label: 'ReleaseTEAM FAQs' },
    ],
  },
];

/** Matches the live site's own Divi desktop/mobile-menu switch, verified live. */
const DESKTOP_BREAKPOINT = '(min-width: 981px)';

export function RTNav() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openItem, setOpenItem] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement | null>(null);

  // Close the mobile menu (and any open accordion) if the viewport is resized
  // past the breakpoint, so a phone rotated to landscape then back does not
  // strand the menu open behind the now-visible desktop nav.
  useEffect(() => {
    const mql = window.matchMedia(DESKTOP_BREAKPOINT);
    const onChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setMobileOpen(false);
        setOpenItem(null);
      }
    };
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape' && event.key !== 'Esc') return;
    if (mobileOpen) {
      setMobileOpen(false);
      setOpenItem(null);
      headerRef.current?.querySelector<HTMLButtonElement>('.rt-nav-toggle')?.focus();
    } else if (openItem) {
      setOpenItem(null);
    }
  };

  return (
    <header className="rt-nav-header" id="rt-nav-header" ref={headerRef} onKeyDown={onKeyDown}>
      <div className="rt-nav-inner">
        <a className="rt-nav-logo-link" href="https://www.releaseteam.com/" aria-label="ReleaseTEAM home">
          <img
            className="rt-nav-logo-img"
            src="/brand/releaseteam-logo-white.png"
            alt="DevOps Consulting, Support, & Software Solutions | ReleaseTEAM"
            width={301}
            height={66}
          />
        </a>

        <button
          type="button"
          className="rt-nav-toggle"
          aria-controls="rt-nav-menu"
          aria-expanded={mobileOpen}
          aria-label="Toggle navigation menu"
          onClick={() => {
            setMobileOpen((open) => {
              const next = !open;
              if (!next) setOpenItem(null);
              return next;
            });
          }}
        >
          <span className="rt-nav-toggle-bar" />
          <span className="rt-nav-toggle-bar" />
          <span className="rt-nav-toggle-bar" />
        </button>

        <nav className="rt-nav-nav" aria-label="Primary">
          <ul className={`rt-nav-menu${mobileOpen ? ' rt-nav-open' : ''}`} id="rt-nav-menu">
            {NAV_ITEMS.map((item) => {
              const hasSub = !!item.sub && item.sub.length > 0;
              const isOpen = openItem === item.id;
              return (
                <li
                  key={item.id}
                  className={`rt-nav-item${hasSub ? ' rt-nav-has-sub' : ''}${isOpen ? ' rt-nav-sub-open' : ''}`}
                >
                  <a
                    href={item.href}
                    className="rt-nav-link"
                    {...(hasSub ? { 'aria-haspopup': true, 'aria-expanded': isOpen } : {})}
                  >
                    {item.label}
                    {hasSub && <span className="rt-nav-caret" aria-hidden="true" />}
                  </a>
                  {hasSub && (
                    <button
                      type="button"
                      className="rt-nav-sub-toggle"
                      aria-label={`Show ${item.label} submenu`}
                      aria-expanded={isOpen}
                      onClick={() => setOpenItem(isOpen ? null : item.id)}
                    />
                  )}
                  {hasSub && (
                    <ul className={`rt-nav-sub${item.wide ? ' rt-nav-sub-wide' : ''}`}>
                      {item.sub?.map((link) => (
                        <li key={link.href}>
                          <a href={link.href}>{link.label}</a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}

            <li className="rt-nav-item rt-nav-cta">
              <a href="https://www.releaseteam.com/contact/" className="rt-nav-link rt-nav-cta-link">
                Contact Us
              </a>
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
}
